import { KeyboardEvent, SyntheticEvent, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { SpotSvg } from '@enterprise/spot';
import classNames from 'classnames';
import styles from './searchBar.module.scss';
import { SearchInput } from './searchInput';

interface SearchbarProps {
    value?: string;
    onChange?: (value: string) => void;
    onSubmit?: (value: string) => void;
    disabled?: boolean;
    isSelfControlled?: boolean;
    secondary?: boolean;
    className?: string;
    searchItemsForAutoComplete?: (value: string) => Promise<string[]>;
}

type Cancelable = { cancel: () => void };

export function SearchBar(props: SearchbarProps) {
    const { value = '', onChange, onSubmit, disabled, className } = props;
    const [autoCompleteItems, setAutoCompleteItems] = useState<string[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const [requestInProgress, setRequestInProgress] = useState<Cancelable>();

    useEffect(() => {
        if (submitted) {
            setSubmitted(false);
            onSubmit?.(value);
        }
    }, [value, submitted]);

    useEffect(() => {
        getAutoCompleteValues(value);
    }, [value]);

    const getAutoCompleteValues = (value: string) => {
        const state = { canceled: false };

        const timeout = setTimeout(async () => {
            if (!value) {
                setAutoCompleteItems([]);
                setRequestInProgress(undefined);
                return;
            }

            const items = (await props.searchItemsForAutoComplete?.(value)) || [];

            if (state.canceled) {
                return;
            }

            setAutoCompleteItems(items);
            setRequestInProgress(undefined);
        }, 1000);

        const cancellable = {
            cancel: () => {
                clearTimeout(timeout);
                state.canceled = true;
                setRequestInProgress(undefined);
            },
        };

        setRequestInProgress(cancellable);
    };

    const onChangeHandler = async (_: SyntheticEvent, newValue: string) => {
        if (value === newValue) {
            return;
        }

        requestInProgress?.cancel();
        onChange?.(newValue);
    };

    const submitHandler = () => {
        requestInProgress?.cancel();
        setAutoCompleteItems([]);
        getAutoCompleteValues('');
        setSubmitted(true);
    };

    const keyPress = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key == 'Enter') {
            submitHandler();
            const { value } = event.target as HTMLTextAreaElement;
            onChange?.(value);
        }
    };

    return (
        <div className={classNames('spot-form', 'spot-search-bar', styles.searchBar, className)}>
            <SearchInput
                data-automation-id="select-mui-searchbar-input"
                disabled={disabled}
                onChange={submitHandler}
                onInputChange={onChangeHandler}
                value={value}
                placeholder="Search…"
                onKeyDown={keyPress}
                filterOptions={() => autoCompleteItems}
                options={autoCompleteItems}
                loading={Boolean(requestInProgress)}
            />
            <Button
                data-automation-id="select-mui-search-button"
                classes={{ root: 'spot-search-bar__search-button' }}
                aria-label="search"
                onClick={submitHandler}
                disabled={disabled}
            >
                <SpotSvg className={'spot-icon'} icon="search" />
            </Button>
        </div>
    );
}
